import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { FiSettings, FiLogOut } from "react-icons/fi";
import { AuthContext } from "../../../Context/AuthContext";
import PageHeader from "../../components/PageHeader";
import DetailGrid from "../../components/DetailGrid";
import Spinner from "../../../Spinner/Spinner";

const fetchAdminProfile = async (token, userId) => {
  const response = await axios.get(
    `${process.env.REACT_APP_SERVER_URL}/users/user/${userId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

const AdminSettingsPage = () => {
  const { user, token, Logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const { data, isLoading, isError } = useQuery(
    ["adminProfile", user?.userId],
    () => fetchAdminProfile(token, user.userId),
    { enabled: !!token && !!user?.userId }
  );

  const changePasswordMutation = useMutation(
    async () => {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/users/change-password/${user.userId}`,
        { oldPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Password changed successfully");
        setOldPassword("");
        setNewPassword("");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || "Could not change password");
      },
    }
  );

  const profile = data?.user;

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!oldPassword.trim() || !newPassword.trim()) {
      toast.error("Enter both old and new password");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    changePasswordMutation.mutate();
  };

  const handleLogout = () => {
    Logout();
    navigate("/Login");
  };

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Your admin account and security"
        icon={FiSettings}
      />

      {isLoading ? (
        <div className="dashboard-card" style={{ padding: 32, textAlign: "center" }}>
          <Spinner />
        </div>
      ) : isError ? (
        <div className="dashboard-banner dashboard-banner--danger">Could not load profile.</div>
      ) : (
        <div className="dashboard-settings-grid">
          <section className="dashboard-card">
            <h2 className="dashboard-card__title">Profile</h2>
            <DetailGrid
              items={[
                { label: "First name", value: profile?.first_name || "—" },
                { label: "Last name", value: profile?.last_name || "—" },
                { label: "Email", value: profile?.email || "—" },
                { label: "Role", value: profile?.role || user?.role || "—" },
              ]}
            />
          </section>

          <section className="dashboard-card">
            <h2 className="dashboard-card__title">Change password</h2>
            <form className="dashboard-form" onSubmit={handlePasswordSubmit}>
              <label className="dashboard-form-field">
                <span>Current password</span>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </label>
              <label className="dashboard-form-field">
                <span>New password</span>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </label>
              <div className="dashboard-form-actions">
                <button
                  type="submit"
                  className="dashboard-btn dashboard-btn--primary"
                  disabled={changePasswordMutation.isLoading}
                >
                  {changePasswordMutation.isLoading ? "Saving…" : "Update password"}
                </button>
              </div>
            </form>
          </section>

          <section className="dashboard-card">
            <h2 className="dashboard-card__title">Session</h2>
            <p className="dashboard-form-hint">Sign out of the marketplace admin dashboard on this device.</p>
            <button type="button" className="dashboard-btn dashboard-btn--danger" onClick={handleLogout}>
              <FiLogOut aria-hidden="true" /> Log out
            </button>
          </section>
        </div>
      )}
    </>
  );
};

export default AdminSettingsPage;
